import {
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import type { CancerDiagnosisEntry } from "../../../../types";
import { formatDisplayDate } from "../../../../utils/displayDate";

// Breathing room kept between the popover and the viewport edge when it flips sides.
const _VIEWPORT_MARGIN = 8;

// Gap between the cell and the popover below it.
const _POPOVER_GAP = 6;

/** Viewport coordinates of the hovered cell, used to place the fixed popover. */
interface AnchorRect {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

/** Which edges the popover had to flip to stay on screen. */
interface Flip {
  right: boolean;
  up: boolean;
}

// The popover is a real <table> so the header and body share one set of column
// widths. Separate per-row grids each size their own columns, which left the
// header labels and the values under them visibly out of line.
const _HEAD_CELL: CSSProperties = {
  padding: "8px 12px",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.5px",
  color: "#6B7280",
  textAlign: "left",
  whiteSpace: "nowrap",
};

const _BODY_CELL: CSSProperties = {
  padding: "8px 12px",
  fontSize: 13,
  color: "#111827",
  // Top, so the code and date stay level with the first line of a wrapped name.
  verticalAlign: "top",
};

// The diagnosis name is the only column allowed to wrap; `anywhere` also breaks a single
// long token rather than widening the table past its max.
const _NAME_CELL: CSSProperties = { ..._BODY_CELL, overflowWrap: "anywhere" };

// `width: 1%` + nowrap shrinks a column to its content, so the diagnosis name keeps every
// remaining pixel instead of wrapping earlier than it needs to. Centred rather than
// right-aligned: both columns are sized by their header ("ICD-10", "DIAGNOSED ON"), which is
// wider than the values, so right alignment left them detached from their labels — a lone
// "-" worst of all. Dates are a fixed width and codes are near it, so rows still read evenly.
const _VALUE_COLUMN: CSSProperties = {
  width: "1%",
  whiteSpace: "nowrap",
  textAlign: "center",
  fontVariantNumeric: "tabular-nums",
};

/**
 * A cell that shows a lead value with a "+N" overflow chip and, on hover, a small
 * three-column table (Diagnosis / the caller's column / Diagnosed). Used for the
 * landing table's Diagnosis (name / ICD-10) and Stage (name / stage) columns over
 * the patient's FHIR-Conditions cancers.
 *
 * The popover opens for a single cancer too: the Diagnosed column carries detail the
 * collapsed cell never shows, so one row is still worth revealing.
 *
 * @param cancers - The patient's cancers (from FHIR Conditions)
 * @param columnLabel - Middle-column header in the popover ("ICD-10" or "STAGE")
 * @param getValue - Maps a cancer to its middle-column value
 * @param lead - Renders the collapsed lead content (first cancer)
 */
function CancerHoverCell({
  cancers,
  columnLabel,
  getValue,
  lead,
}: {
  cancers: CancerDiagnosisEntry[];
  columnLabel: string;
  getValue: (c: CancerDiagnosisEntry) => string;
  lead: (first: CancerDiagnosisEntry, extra: number) => ReactNode;
}) {
  const [anchor, setAnchor] = useState<AnchorRect | null>(null);
  const [flip, setFlip] = useState<Flip>({ right: false, up: false });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Flip to the cell's other edge when the default placement would run off screen. Measured
  // once open (before paint) rather than inferred from the column, because the popover's
  // size depends on the longest diagnosis name in the table.
  useLayoutEffect(() => {
    /**
     * Keep the same object when the flags are unchanged so React can bail out
     * of the re-render (a fresh literal never compares equal).
     *
     * @param next - The newly computed flip flags
     * @returns A state updater preserving identity when nothing changed
     */
    const applyFlip = (next: Flip) => (prev: Flip) =>
      prev.right === next.right && prev.up === next.up ? prev : next;
    if (!anchor) {
      setFlip(applyFlip({ right: false, up: false }));
      return;
    }
    const element = popoverRef.current;
    if (!element) return;
    const rect = element.getBoundingClientRect();
    setFlip(
      applyFlip({
        right: rect.right > window.innerWidth - _VIEWPORT_MARGIN,
        up: rect.bottom > window.innerHeight - _VIEWPORT_MARGIN,
      }),
    );
  }, [anchor]);

  /**
   * Capture the hovered cell's viewport position so the popover can be placed.
   *
   * @returns Nothing
   */
  const openPopover = () => {
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (rect) {
      setAnchor({
        top: rect.top,
        bottom: rect.bottom,
        left: rect.left,
        right: rect.right,
      });
    }
  };

  if (!cancers.length) {
    return <span className="text-sm font-medium" style={{ color: "#9CA3AF" }}>—</span>;
  }
  const extra = cancers.length - 1;
  return (
    <div
      ref={wrapperRef}
      // inline-block so the wrapper follows the column's text-align (the Stage
      // column centres, the Diagnosis column stays left) instead of a
      // fit-content block always hugging the left edge.
      style={{ display: "inline-block", textAlign: "left" }}
      onMouseEnter={openPopover}
      onMouseLeave={() => setAnchor(null)}
    >
      {lead(cancers[0], extra)}
      {anchor && (
        <div
          ref={popoverRef}
          style={{
            // Fixed, not absolute: the table card clips its scroll region
            // (`custom-table.scss` — overflow hidden/auto), so an absolutely
            // positioned popover is cut off on the lower rows. Same approach the
            // disease-status hover in `patientCells` already uses. Escaping the
            // scroll box also makes the viewport-based flip checks meaningful.
            position: "fixed",
            // Anchoring the far edge (rather than offsetting by a measured size)
            // flips the popover without needing its width or height.
            ...(flip.up
              ? { bottom: window.innerHeight - anchor.top + _POPOVER_GAP }
              : { top: anchor.bottom + _POPOVER_GAP }),
            ...(flip.right
              ? { right: Math.max(_VIEWPORT_MARGIN, window.innerWidth - anchor.right) }
              : { left: anchor.left }),
            zIndex: 50,
            // Nothing in here is clickable, and it now opens over the rows below on every
            // hover — so let clicks through instead of blocking the rows it covers.
            pointerEvents: "none",
            // Sized to the header row (the three labels are nowrap) rather than to the
            // longest diagnosis name — past this width the name wraps instead, which keeps
            // the panel from covering half the table on every hover.
            minWidth: 300,
            maxWidth: 440,
            backgroundColor: "var(--color-card, #fff)",
            border: "1px solid var(--color-border, #E5E7EB)",
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            overflow: "hidden",
            textAlign: "left",
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr
                style={{
                  borderBottom: "1px solid var(--color-border, #E5E7EB)",
                }}
              >
                <th style={_HEAD_CELL}>DIAGNOSIS</th>
                <th style={{ ..._HEAD_CELL, ..._VALUE_COLUMN }}>
                  {columnLabel}
                </th>
                <th style={{ ..._HEAD_CELL, ..._VALUE_COLUMN }}>
                  DIAGNOSED ON
                </th>
              </tr>
            </thead>
            <tbody>
              {cancers.map((c, i) => (
                <tr
                  key={`${c.code}-${i}`}
                  style={{
                    borderBottom:
                      i < cancers.length - 1
                        ? "1px solid var(--color-border, #F3F4F6)"
                        : "none",
                  }}
                >
                  <td style={_NAME_CELL}>{c.description || c.code}</td>
                  <td style={{ ..._BODY_CELL, ..._VALUE_COLUMN }}>
                    {getValue(c) || "-"}
                  </td>
                  <td
                    style={{
                      ..._BODY_CELL,
                      ..._VALUE_COLUMN,
                      color: c.diagnosedOn ? "#111827" : "#9CA3AF",
                    }}
                  >
                    {formatDisplayDate(c.diagnosedOn, "-")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** "+N" overflow chip shown after the lead value. */
function OverflowChip({ extra }: { extra: number }) {
  if (extra <= 0) return null;
  return (
    <span className="text-xs" style={{ color: "#6B7280", marginLeft: 4 }}>
      +{extra}
    </span>
  );
}

export { CancerHoverCell, OverflowChip };
